import { useEffect } from 'react';
import { useNotifications, type Severity } from '@/state/notifications';
import { useSurfaces } from '@/state/surfaces';
import { cn } from '@/lib/cn';

const AUTO_DISMISS_MS: Record<Severity, number> = {
  passive: 4000,
  info: 6000,
  important: 12000,
  urgent: 30000,
};

const SEVERITY_STYLES: Record<Severity, string> = {
  passive: 'border-[var(--border-subtle)] text-[var(--text-secondary)]',
  info: 'border-[var(--border-strong)] text-[var(--text-primary)]',
  important: 'border-[var(--accent-primary)] text-[var(--accent-primary)]',
  urgent: 'border-[var(--critical)] text-[var(--critical)]',
};

export function NotificationDock() {
  const items = useNotifications((s) => s.items);
  const dismiss = useNotifications((s) => s.dismiss);
  const clearAll = useNotifications((s) => s.clearAll);
  const open = useSurfaces((s) => s.open);

  useEffect(() => {
    const timers = items.map((n) => {
      const elapsed = Date.now() - n.createdAt;
      const remaining = Math.max(0, AUTO_DISMISS_MS[n.severity] - elapsed);
      return window.setTimeout(() => dismiss(n.id), remaining);
    });
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [items, dismiss]);

  return (
    <div className="pointer-events-none fixed right-3 top-14 z-30 flex w-[300px] max-w-[80vw] flex-col gap-2">
      {items.length >= 3 && (
        <button
          onClick={clearAll}
          className="pointer-events-auto self-end rounded border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--text-muted)] hover:text-[var(--accent-primary)]"
        >
          clear all ({items.length})
        </button>
      )}
      {items.map((n) => (
        <button
          key={n.id}
          onClick={() => {
            open(n.surfaceId);
            dismiss(n.id);
          }}
          className={cn(
            'pointer-events-auto rounded border bg-[var(--bg-elevated)] px-3 py-2 text-left text-mono-xs shadow-xl transition-all',
            'animate-[slideIn_300ms_ease-out_1]',
            SEVERITY_STYLES[n.severity],
          )}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">
            {n.surfaceId === 'x-clone' ? 'Y' : n.surfaceId}
          </div>
          <div className="mt-0.5 text-mono-xs leading-snug">{n.text}</div>
        </button>
      ))}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(16px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
